import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const PlayerCountModal = ({ 
  onClose, 
  onSelectCount, 
  isLoading 
}) => {
  const { t } = useLanguage();
  const [selectedCount, setSelectedCount] = React.useState(4); // Default to 4 players

  // Ensure clean state when modal is shown
  useEffect(() => {
    return () => {
      // Cleanup function when modal unmounts
      setSelectedCount(4); // Reset to default
    };
  }, []);

  const handleSelectCount = (count) => {
    setSelectedCount(count);
  };

  const handleConfirm = () => {
    onSelectCount(selectedCount);
  };

  const handleCancel = () => {
    // Ensure proper cleanup before closing
    setSelectedCount(4);
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{t('playerCount.title')}</h2>
          <button 
            className="close-btn" 
            onClick={handleCancel}
            disabled={isLoading}
            aria-label={t('common.close')}
          >
            &times;
          </button>
        </div>
        
        <div className="modal-content">
          <div className="player-count-selector">
            <p>{t('playerCount.prompt')}</p>
            <div className="count-buttons">
              {[2, 3, 4].map(count => (
                <button
                  key={count}
                  type="button"
                  onClick={() => handleSelectCount(count)}
                  className={`count-btn ${selectedCount === count ? 'active' : ''}`}
                  aria-pressed={selectedCount === count}
                  disabled={isLoading}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="action-btn cancel-btn" 
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="action-btn confirm-btn" 
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? t('playerCount.creating') : t('playerCount.createWithCount', { count: selectedCount })}
          </button>
        </div>
      </div>
      
      <style>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        
        .modal-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
          width: 90%;
          max-width: 500px;
          overflow: hidden;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          color: #111827;
        }
        
        .close-btn {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }
        
        .modal-content {
          padding: 1.5rem;
        }
        
        .player-count-selector p {
          margin-top: 0;
          margin-bottom: 1rem;
          color: #4b5563;
        }
        
        .count-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }
        
        .count-btn {
          width: 4rem;
          height: 4rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          cursor: pointer;
          border: 2px solid #d1d5db;
          background-color: white;
          color: #4b5563;
          transition: all 0.2s ease;
        }
        
        .count-btn:hover {
          background-color: #f3f4f6;
        }
        
        .count-btn.active {
          background-color: #10b981;
          color: white;
          border-color: #10b981;
        }
        
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
          background-color: #f9fafb;
        }
        
        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .cancel-btn {
          background-color: white;
          color: #4b5563;
          border: 1px solid #d1d5db;
        }
        
        .cancel-btn:hover {
          background-color: #f3f4f6;
        }
        
        .confirm-btn {
          background-color: #10b981;
          color: white;
          border: 1px solid #10b981;
        }
        
        .confirm-btn:hover {
          background-color: #059669;
        }
        
        .confirm-btn:disabled,
        .cancel-btn:disabled,
        .count-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default PlayerCountModal; 